import React, { useState } from 'react';
import { deriveSiteCode, isValidSiteCode } from '../utils/conditionIds';
import { ReviewRoutingSection } from '../components/conditions/ReviewRoutingSection';
import { ViewAsAccessSection } from '../components/ViewAsAccessSection';
import { ApiAccessSection } from '../../../components/Settings/ApiAccessSection';
import { LearningLibrarySection } from '../components/LearningLibrarySection';
import { MemberFormFields } from '../components/members/MemberFormFields';
import { MembersList } from '../components/members/MembersList';
import { emptyMemberForm, memberToForm, memberFormToPayload, validateMemberForm, memberUnitLabel, unitForRole } from '../components/members/memberForm';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Slider,
  Stack,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { emitOnboardingStep } from '../../../onboarding/onboardingEvents';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BadgeIcon from '@mui/icons-material/Badge';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../../../contexts/AuthContext';
import { useUser } from '../../../contexts/UserContext';
import { REPS_COLORS } from '../utils/constants';
import { DEFAULT_SCORING, normalizeScoring } from '../utils/conditionLoad';
import { recomputeOrganizationSnapshots } from '../../../services/oeSnapshotService';
import { onePerPersonFor } from '../../../services/responseService';
import { isCrew } from '../utils/rollups';
import {
  useUserOrganizations,
  useOrganization,
  useOrganizationActions,
} from '../hooks/useOrganization';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  ROLE_DEFINITIONS,
  assignRole,
  removeRole,
  subscribeToOrganizationMembers,
  setToolAccessEmail,
} from '../../../services/roleService';
import {
  createInvite,
  findExistingInvite,
  subscribeToOrganizationInvites,
  revokeInvite,
  isAlreadyMember,
} from '../../../services/inviteService';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import OrgImportDialog from '../components/OrgImportDialog';
import RosterImportDialog from '../components/RosterImportDialog';
import { provisionOrgUser, updateOrgMember } from '../../../services/orgUserService';

// --- Dialogs ---

function CreateOrgDialog({ open, onClose, onSubmit, onImport }) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), industry: industry.trim() });
    setName('');
    setIndustry('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Organization</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Organization Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {/* Tim D07 (P4-14): the import option lives inside "+ New Organization", between the first and second fields. */}
        <Card variant="outlined" sx={{ my: 1.5, bgcolor: 'action.hover' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You can choose to enter your organization manually or you can import your organization structure. To import your organization structure, use the Import Organization button provided below. Otherwise, build your organization manually using the fields below.
            </Typography>
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={(e) => { e.currentTarget.blur(); onImport(); }} data-tour="import-org">
              Import Organization
            </Button>
          </CardContent>
        </Card>
        <TextField
          margin="dense"
          label="Industry"
          fullWidth
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Site code field (P4-19 / P2-06): 2–4 letters, auto-suggested from the name, unique in the org. */
function SiteCodeField({ value, onChange, name, takenCodes, autoFill }) {
  const suggested = deriveSiteCode(name, takenCodes);
  const shown = value;
  const invalid = shown !== '' && !isValidSiteCode(shown);
  const duplicate = shown !== '' && takenCodes.includes(shown);
  return (
    <TextField
      margin="dense"
      label="Site code (for OE Condition IDs)"
      fullWidth
      value={shown}
      onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))}
      placeholder={suggested}
      error={invalid || duplicate}
      inputProps={{ style: { fontFamily: '"Roboto Mono", monospace' }, maxLength: 4 }}
      helperText={
        duplicate
          ? 'Another site already uses this code.'
          : invalid
            ? '2–4 letters or digits.'
            : `IDs look like 26-${shown || suggested}-0001. Leave blank to use ${suggested}.`
      }
      onBlur={() => autoFill && !shown && onChange(suggested)}
    />
  );
}

function AddSiteDialog({ open, onClose, onSubmit, sites = [] }) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [code, setCode] = useState('');
  const takenCodes = sites.map((s) => (isValidSiteCode(s.code) ? s.code : deriveSiteCode(s.name)));

  const handleSubmit = () => {
    if (!name.trim()) return;
    const finalCode = isValidSiteCode(code) && !takenCodes.includes(code) ? code : deriveSiteCode(name, takenCodes);
    onSubmit({ name: name.trim(), location: location.trim(), code: finalCode });
    setName('');
    setLocation('');
    setCode('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Site</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Site Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Location"
          fullWidth
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
        <SiteCodeField value={code} onChange={setCode} name={name} takenCodes={takenCodes} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim()}>
          Add Site
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddDepartmentDialog({ open, onClose, onSubmit, sites }) {
  const [name, setName] = useState('');
  const [siteId, setSiteId] = useState('');

  const handleSubmit = () => {
    if (!name.trim() || !siteId) return;
    onSubmit({ name: name.trim(), siteId });
    setName('');
    setSiteId('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Department</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Department Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Site"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
        >
          <option value="">Select a site...</option>
          {(sites || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim() || !siteId}>
          Add Department
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddTeamDialog({ open, onClose, onSubmit, departments }) {
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [nwtType, setNwtType] = useState('crew'); // P2-38 / P4-20

  const handleSubmit = () => {
    if (!name.trim() || !departmentId) return;
    onSubmit({ name: name.trim(), departmentId, nwtType });
    setName('');
    setDepartmentId('');
    setNwtType('crew');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Natural Work Team (NWT)</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="NWT Name (e.g., Shift Crew A)"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Department"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
        >
          <option value="">Select a department...</option>
          {(departments || []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label="NWT type"
          fullWidth
          select
          SelectProps={{ native: true }}
          value={nwtType}
          onChange={(e) => setNwtType(e.target.value)}
          helperText="Operating crews weigh equally in their department's score; other teams weigh by headcount."
        >
          <option value="crew">Operating crew (shift / rotating)</option>
          <option value="team">Team (day, staff, support)</option>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim() || !departmentId}>
          Add NWT
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditSiteDialog({ open, onClose, onSubmit, site, sites = [] }) {
  const [name, setName] = useState(site?.name || '');
  const [location, setLocation] = useState(site?.location || '');
  const [code, setCode] = useState(site?.code || '');
  const takenCodes = sites.filter((s) => s.id !== site?.id).map((s) => (isValidSiteCode(s.code) ? s.code : deriveSiteCode(s.name)));

  React.useEffect(() => {
    if (site) {
      setName(site.name || '');
      setLocation(site.location || '');
      setCode(site.code || '');
    }
  }, [site]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    const finalCode = isValidSiteCode(code) && !takenCodes.includes(code) ? code : deriveSiteCode(name, takenCodes);
    onSubmit({ name: name.trim(), location: location.trim(), code: finalCode });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Site</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Site Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Location"
          fullWidth
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
        <SiteCodeField value={code} onChange={setCode} name={name} takenCodes={takenCodes} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditDepartmentDialog({ open, onClose, onSubmit, department, sites }) {
  const [name, setName] = useState(department?.name || '');
  const [siteId, setSiteId] = useState(department?.siteId || '');

  React.useEffect(() => {
    if (department) {
      setName(department.name || '');
      setSiteId(department.siteId || '');
    }
  }, [department]);

  const handleSubmit = () => {
    if (!name.trim() || !siteId) return;
    onSubmit({ name: name.trim(), siteId });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Department</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Department Name"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Site"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
        >
          <option value="">Select a site...</option>
          {(sites || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim() || !siteId}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditTeamDialog({ open, onClose, onSubmit, team, departments }) {
  const [name, setName] = useState(team?.name || '');
  const [departmentId, setDepartmentId] = useState(team?.departmentId || '');
  const [nwtType, setNwtType] = useState(team ? (isCrew(team) ? 'crew' : 'team') : 'crew');

  React.useEffect(() => {
    if (team) {
      setName(team.name || '');
      setDepartmentId(team.departmentId || '');
      setNwtType(isCrew(team) ? 'crew' : 'team');
    }
  }, [team]);

  const handleSubmit = () => {
    if (!name.trim() || !departmentId) return;
    onSubmit({ name: name.trim(), departmentId, nwtType });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Team</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="NWT Name (e.g., Shift Crew A)"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Department"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
        >
          <option value="">Select a department...</option>
          {(departments || []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label="NWT type"
          fullWidth
          select
          SelectProps={{ native: true }}
          value={nwtType}
          onChange={(e) => setNwtType(e.target.value)}
          helperText="Operating crews weigh equally in their department's score; other teams weigh by headcount."
        >
          <option value="crew">Operating crew (shift / rotating)</option>
          <option value="team">Team (day, staff, support)</option>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!name.trim() || !departmentId}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ open, onClose, onConfirm, itemName }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete <strong>{itemName}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Thresholds Editor ---

/**
 * Get severity label for a threshold value
 */
function getThresholdSeverityLabel(value) {
  if (value <= 3) return { label: 'Strict', color: '#c62828' };
  if (value <= 5) return { label: 'Moderate', color: '#ef6c00' };
  if (value <= 7) return { label: 'Standard', color: '#1565c0' };
  return { label: 'Lenient', color: '#2e7d32' };
}

/**
 * P2-35 — how approved OE Conditions feed the REPS scores ("Reality"). Every field is a tunable
 * from docs/beta/OE-SCORING.md; the defaults are the ones proposed to Tim (⚑Q-17b).
 */
function ScoringSettingsEditor({ scoring, onSave, saving, orgId }) {
  const toForm = (src) => {
    const n = normalizeScoring(src);
    return {
      mode: n.mode,
      I: String(n.conditionWeights.I), II: String(n.conditionWeights.II), III: String(n.conditionWeights.III), IV: String(n.conditionWeights.IV),
      conditionPer: String(n.conditionPer), pointsPerUnit: String(n.pointsPerUnit), maxSharePct: String(Math.round(n.maxShare * 100)), decayDays: String(n.decayDays),
    };
  };
  const [form, setForm] = useState(() => toForm(scoring));
  const [dirty, setDirty] = useState(false);
  const [recompute, setRecompute] = useState({ busy: false, msg: null, severity: 'info' });
  React.useEffect(() => { setForm(toForm(scoring)); setDirty(false); }, [scoring]);
  const conditionsOnly = form.mode === 'conditions';
  const runRecompute = async () => {
    setRecompute({ busy: true, msg: null, severity: 'info' });
    try {
      const r = await recomputeOrganizationSnapshots(orgId);
      const failed = r.sites.filter((x) => !x.ok).length;
      setRecompute({ busy: false, severity: failed ? 'warning' : 'success', msg: `Recomputed the organization score and ${r.sites.length - failed} of ${r.sites.length} site scores from today's approved OE Conditions.${failed ? ` ${failed} site(s) skipped.` : ''}` });
    } catch (err) {
      setRecompute({ busy: false, severity: 'error', msg: err.message || 'Recompute failed.' });
    }
  };

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setDirty(true); };
  const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : NaN; };
  const invalid = ['I', 'II', 'III', 'IV', 'conditionPer', 'pointsPerUnit', 'maxSharePct', 'decayDays'].some((k) => Number.isNaN(num(form[k])) || num(form[k]) < 0)
    || num(form.conditionPer) < 1 || num(form.maxSharePct) > 100;

  const handleSave = () => {
    onSave(normalizeScoring({
      mode: form.mode,
      conditionWeights: { I: num(form.I), II: num(form.II), III: num(form.III), IV: num(form.IV) },
      conditionPer: num(form.conditionPer), pointsPerUnit: num(form.pointsPerUnit), maxShare: num(form.maxSharePct) / 100, decayDays: num(form.decayDays),
    }));
    setDirty(false);
  };
  const reset = () => { setForm(toForm(DEFAULT_SCORING)); setDirty(true); };
  const cap = (num(form.maxSharePct) / 10).toFixed(1);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>REPS scoring</Typography>
        <TextField select size="small" label="What moves the REPS scores" value={form.mode} onChange={set('mode')} sx={{ mb: 2, minWidth: 360 }}>
          <MenuItem value="conditions">Approved OE Conditions only (Drift, Traps, Roadblocks, Landmines)</MenuItem>
          <MenuItem value="blend">Surveys + capped condition load (Beta-1 blend)</MenuItem>
        </TextField>
        {conditionsOnly ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            The score of each quadrant is the load of <strong>approved OE Conditions</strong> tagged with it: each condition counts its
            severity weight, normalized per {form.conditionPer || '…'} people in scope, times the points per weighted condition, 0–10.
            Resolved conditions fade out over {form.decayDays || '…'} days. Surveys measure <strong>perception</strong> and are shown
            beside the score for divergence; they never move it.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Surveys give the <strong>Perception</strong> score. Approved/Active OE Conditions add a <strong>Reality</strong> load on the
            quadrants they are tagged with: each condition counts its severity weight, normalized per {form.conditionPer || '…'} people in
            scope, and the total is capped so conditions alone can add at most {Number.isNaN(Number(cap)) ? '…' : cap} points of 10.
            Resolved conditions fade out over {form.decayDays || '…'} days.
          </Typography>
        )}
        <Typography variant="subtitle2" gutterBottom>Severity weights</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {['I', 'II', 'III', 'IV'].map((k) => (
            <Grid item xs={6} sm={3} key={k}>
              <TextField label={`Severity ${k}`} type="number" size="small" fullWidth value={form[k]} onChange={set(k)} inputProps={{ min: 0, step: 0.5 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}><TextField label="Per how many people" type="number" size="small" fullWidth value={form.conditionPer} onChange={set('conditionPer')} inputProps={{ min: 1 }} /></Grid>
          <Grid item xs={6} sm={3}><TextField label="Points per weighted condition" type="number" size="small" fullWidth value={form.pointsPerUnit} onChange={set('pointsPerUnit')} inputProps={{ min: 0, step: 0.1 }} /></Grid>
          {!conditionsOnly && <Grid item xs={6} sm={3}><TextField label="Max share of score (%)" type="number" size="small" fullWidth value={form.maxSharePct} onChange={set('maxSharePct')} inputProps={{ min: 0, max: 100 }} /></Grid>}
          <Grid item xs={6} sm={3}><TextField label="Fade-out after Resolved (days)" type="number" size="small" fullWidth value={form.decayDays} onChange={set('decayDays')} inputProps={{ min: 0 }} /></Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Example with these settings: one Severity {'II'} condition tagged Safety in a 100-person site adds {(num(form.II) * (100 / Math.max(num(form.conditionPer) || 1, 1)) * (num(form.pointsPerUnit) || 0)).toFixed(1)} to that site's Safety score
          {conditionsOnly ? '.' : ' (before the cap).'} Dashboards compute live; stored history is rewritten only by "Recompute scores now".
        </Typography>
        {recompute.msg && <Alert severity={recompute.severity} sx={{ mt: 1.5 }} onClose={() => setRecompute((r) => ({ ...r, msg: null }))}>{recompute.msg}</Alert>}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={!dirty || invalid || saving}>{saving ? 'Saving…' : 'Save scoring'}</Button>
        <Button onClick={reset} disabled={saving}>Restore defaults</Button>
        <Button variant="outlined" onClick={runRecompute} disabled={recompute.busy || dirty || !orgId} title={dirty ? 'Save first' : 'Rewrite the organization and site snapshots with the saved settings'}>
          {recompute.busy ? 'Recomputing…' : 'Recompute scores now'}
        </Button>
      </CardActions>
    </Card>
  );
}

/** Tim 26 Oct (A17 / Q-22): one response per person per survey. Default on for real organizations, off in the Magnum sandboxes. */
function SurveyRulesEditor({ org, onSave, saving }) {
  const current = onePerPersonFor(org);
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Survey rules</Typography>
        <FormControlLabel
          control={<Switch checked={current} disabled={saving} onChange={(e) => onSave({ surveys: { ...(org?.surveys || {}), oneResponsePerPerson: e.target.checked } })} />}
          label="One response per person per survey"
        />
        <Typography variant="body2" color="text.secondary">
          {current
            ? 'Each member can submit a survey once; a second attempt is refused. Anonymous surveys are not limited.'
            : 'Members can submit the same survey repeatedly — the sandbox setting, so testers can watch the numbers move.'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ThresholdsEditor({ thresholds, onSave, saving }) {
  const [values, setValues] = useState(thresholds || {
    reliability: 6,
    efficiency: 6,
    productivity: 6,
    safety: 6,
  });
  const [dirty, setDirty] = useState(false);

  // Sync with incoming thresholds when org data changes
  React.useEffect(() => {
    if (thresholds) {
      setValues(thresholds);
    }
  }, [thresholds]);

  const handleChange = (quadrant, newValue) => {
    setValues((prev) => ({ ...prev, [quadrant]: newValue }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave(values);
    setDirty(false);
  };

  const quadrants = [
    { key: 'reliability', label: 'Reliability', color: REPS_COLORS.reliability, abbr: 'R' },
    { key: 'efficiency', label: 'Efficiency', color: REPS_COLORS.efficiency, abbr: 'E' },
    { key: 'productivity', label: 'Productivity', color: REPS_COLORS.productivity, abbr: 'P' },
    { key: 'safety', label: 'Safety', color: REPS_COLORS.safety, abbr: 'S' },
  ];

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Organizational Entropy (OE) Warning Thresholds</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Set the warning threshold (1-10) for each REPS quadrant. Warnings trigger when OE scores
          exceed these values. Lower thresholds are stricter and trigger warnings earlier.
        </Typography>

        {/* Visual preview bar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 2 }}>
          {quadrants.map(({ key, abbr, color }) => {
            const severity = getThresholdSeverityLabel(values[key]);
            return (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: `${color}15`,
                  border: `1px solid ${color}40`,
                }}
              >
                <Typography variant="caption" sx={{ color, fontWeight: 'bold' }}>
                  {abbr}
                </Typography>
                <Typography variant="h6" sx={{ color, lineHeight: 1.2 }}>
                  {values[key]}
                </Typography>
                <Typography variant="caption" sx={{ color: severity.color, fontSize: '0.65rem' }}>
                  {severity.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {quadrants.map(({ key, label, color }) => {
          const severity = getThresholdSeverityLabel(values[key]);
          return (
            <Box key={key} sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ color }}>
                  {label}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={severity.label}
                    size="small"
                    sx={{ bgcolor: `${severity.color}20`, color: severity.color, fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={values[key]}
                    size="small"
                    sx={{ bgcolor: color, color: '#fff', fontWeight: 'bold' }}
                  />
                </Stack>
              </Stack>
              <Slider
                value={values[key]}
                onChange={(_, v) => handleChange(key, v)}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                sx={{
                  color,
                  '& .MuiSlider-markLabel': { fontSize: '0.7rem' },
                }}
              />
            </Box>
          );
        })}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save Thresholds
        </Button>
        {dirty && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Unsaved changes
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// --- Organization Detail View ---

function AssignRoleDialog({ open, onClose, onSubmit, sites, departments, teams }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('respondent');
  const [scopeId, setScopeId] = useState('');

  const roleDef = ROLE_DEFINITIONS[role];
  // P4-22: an invited Individual Contributor also needs their NWT (unitForRole → 'team').
  const needsScope = Boolean(unitForRole(role));

  const getScopeOptions = () => {
    // Qualify shared names ("Operations" ×10 sites) with their site/department.
    const siteName = (id) => (sites || []).find((s) => s.id === id)?.name || '';
    if (role === 'site_manager') return (sites || []).map((s) => ({ id: s.id, label: s.name }));
    if (role === 'department_lead') {
      return (departments || []).map((d) => ({ id: d.id, label: `${d.name} — ${siteName(d.siteId)}` }));
    }
    if (role === 'team_lead' || role === 'respondent') {
      return (teams || []).map((t) => {
        const dept = (departments || []).find((d) => d.id === t.departmentId);
        return { id: t.id, label: `${t.name} — ${dept?.name || '?'}, ${siteName(dept?.siteId)}` };
      });
    }
    return [];
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setScopeId('');
  };

  const handleSubmit = () => {
    if (!email.trim()) return;
    if (needsScope && !scopeId) return;
    onSubmit({ email: email.trim(), role, scopeId: scopeId || null });
    setEmail('');
    setRole('respondent');
    setScopeId('');
  };

  const scopeOptions = getScopeOptions();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Role to User</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="User Email"
          type="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          helperText="Enter the email of the user to assign a role"
        />
        <TextField
          margin="dense"
          label="Role"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={role}
          onChange={(e) => handleRoleChange(e.target.value)}
        >
          {Object.entries(ROLE_DEFINITIONS).map(([key, def]) => (
            <option key={key} value={key}>
              {def.label}
            </option>
          ))}
        </TextField>
        {needsScope && (
          <TextField
            margin="dense"
            label={`Select ${role === 'respondent' ? 'Natural Work Team (NWT)' : (roleDef.scopeLabel || roleDef.scopeType)}`}
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
          >
            <option value="">Select {role === 'respondent' ? 'Natural Work Team (NWT)' : (roleDef.scopeLabel || roleDef.scopeType)}...</option>
            {scopeOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </TextField>
        )}
        {roleDef && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {roleDef.description}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!email.trim() || (needsScope && !scopeId)}
        >
          Assign Role
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ROLE_COLORS = {
  org_admin: '#c62828',
  site_manager: '#1565c0',
  department_lead: '#00897b',
  team_lead: '#ef6c00',
  respondent: '#757575',
};

// One credential row with a copy button.
function CredRow({ label, value, onCopy, mono }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</Typography>
      </Box>
      <IconButton size="small" onClick={onCopy} title="Copy"><ContentCopyIcon fontSize="small" /></IconButton>
    </Box>
  );
}

// Add / Edit member dialogs (Beta-1 plan P4-09 / P4-13 / P4-23) share MemberFormFields.
function EditMemberDialog({ open, member, onClose, onSubmit, saving, organization }) {
  const [form, setForm] = useState(emptyMemberForm());
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open && member) {
      setForm(memberToForm(member, organization));
      setErrors({});
    }
  }, [open, member, organization]);

  const handleSave = () => {
    const errs = validateMemberForm(form, { requireEmail: false });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    onSubmit(memberFormToPayload(form, organization));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EditIcon color="primary" />
          <Typography variant="h6">Edit Member</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <MemberFormFields form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} errors={errors} organization={organization} mode="edit" disabled={saving} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Takes effect the next time they load the app. Their password and login are unchanged.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddMemberDialog({ open, onClose, organization, onCreate, creating, result, error, onReset }) {
  const [form, setForm] = useState(emptyMemberForm());
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open && !result) {
      setForm(emptyMemberForm());
      setErrors({});
    }
  }, [open, result]);

  const handleCreate = () => {
    const errs = validateMemberForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    onCreate(memberFormToPayload(form, organization));
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (_) { /* clipboard unavailable */ } };
  const grantedUnit = result ? memberUnitLabel({ role: result.role, scopeId: result.scopeId, scopeIds: result.scopeIds, nwtId: result.nwtId, deptId: result.deptId, siteId: result.siteId }, organization) : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6">Add Member</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              {result.isNewUser ? 'Account created.' : 'User already existed — role assigned.'}{' '}
              {result.displayName ? `${result.displayName} ` : ''}can now access REPSit.
              {result.notified === false ? ' No email was sent (quiet add).' : ''}
            </Alert>
            <Stack spacing={1.5}>
              <CredRow label="Email" value={result.email} onCopy={() => copy(result.email)} />
              {result.isNewUser && result.tempPassword && (
                <CredRow label="Temporary password" value={result.tempPassword} onCopy={() => copy(result.tempPassword)} mono />
              )}
            </Stack>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Role granted: <strong>{ROLE_DEFINITIONS[result.role]?.label || result.role}</strong>
              {grantedUnit ? <> — <strong>{grantedUnit}</strong></> : null}
            </Typography>
            {result.isNewUser && result.tempPassword && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {result.notified === false
                  ? 'Hand these details over yourself — nothing was emailed. '
                  : 'These details have also been emailed to the member. '}
                They&rsquo;ll be asked to reset the password on first login. The password won&rsquo;t be shown here again.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <MemberFormFields form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} errors={errors} organization={organization} mode="add" disabled={creating} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Creates a real login and assigns this role. Prefer to let them self-onboard? Use &ldquo;Invite Users&rdquo; below instead.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button onClick={onReset}>Add another</Button>
            <Button variant="contained" onClick={onClose}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={creating}>Cancel</Button>
            <Button
              variant="contained" onClick={handleCreate} disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />}
            >
              {creating ? 'Creating…' : form.notify === false ? 'Create account quietly' : 'Create account & send welcome email'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function MembersRolesSection({ orgId, organization }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [createError, setCreateError] = useState(null);

  React.useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const unsubscribe = subscribeToOrganizationMembers(orgId, (m) => {
      setMembers(m);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [orgId]);

  const getScopeName = (member) => {
    if (!member.scopeId) return null;
    if (member.scopeType === 'site') {
      const site = (organization.sites || []).find((s) => s.id === member.scopeId);
      return site ? site.name : 'Unknown site';
    }
    if (member.scopeType === 'department') {
      const dept = (organization.departments || []).find((d) => d.id === member.scopeId);
      if (!dept) return 'Unknown dept';
      const site = (organization.sites || []).find((s) => s.id === dept.siteId);
      return site ? `${dept.name} — ${site.name}` : dept.name;
    }
    if (member.scopeType === 'team') {
      const team = (organization.teams || []).find((t) => t.id === member.scopeId);
      if (!team) return 'Unknown team';
      const dept = (organization.departments || []).find((d) => d.id === team.departmentId);
      const site = dept ? (organization.sites || []).find((s) => s.id === dept.siteId) : null;
      return [team.name, dept?.name, site?.name].filter(Boolean).join(' — ');
    }
    return null;
  };

  // Create a real Auth account + assign the role via the hugoCreateOrgUser function.
  // Returns generated credentials to share. Members list refreshes via subscription.
  const handleCreateMember = async (payload) => {
    setCreating(true);
    setCreateError(null);
    try {
      const res = await provisionOrgUser({ ...payload, organizationId: orgId });
      setCreatedResult({ ...res, ...payload, ...res });
      // Onboarding: successfully adding a member = "add your people".
      emitOnboardingStep('add-your-people');
    } catch (err) {
      // Surface a friendly message if the function isn't deployed yet.
      const msg = /not-found|internal|functions\//i.test(err.message || '')
        ? `Could not reach the account-creation function. Make sure it's deployed (firebase deploy --only functions:hugoCreateOrgUser). [${err.message}]`
        : (err.message || 'Could not create the account.');
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  const closeAddDialog = () => {
    setAssignOpen(false);
    setCreatedResult(null);
    setCreateError(null);
  };

  const resetAddDialog = () => {
    setCreatedResult(null);
    setCreateError(null);
  };

  const handleRemoveRole = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setActionError(null);
    try {
      // Through the callable so org admins (not just superadmins) can remove.
      await updateOrgMember({ organizationId: orgId, uid: deleteConfirm.uid, action: 'remove' });
      setDeleteConfirm(null);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditRole = async (payload) => {
    if (!editMember) return;
    setSaving(true);
    setActionError(null);
    try {
      const { email: _email, notify: _notify, ...rest } = payload;
      await updateOrgMember({ organizationId: orgId, uid: editMember.uid, action: 'assign', ...rest });
      setEditMember(null);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion defaultExpanded data-tour="members-roles">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BadgeIcon color="primary" />
          <Typography variant="h6">Members & Roles</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your team members — everyone with a login in this organization, their access level and the site, department or Natural Work Team they belong to. Search or filter by role and unit; edit a member to change their details or scope.
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <MembersList members={members} organization={organization} onEdit={(m) => setEditMember(m)} onRemove={(m) => setDeleteConfirm(m)} disabled={saving} />
        )}

        <Button
          startIcon={<PersonAddIcon />}
          variant="outlined"
          onClick={() => { setCreatedResult(null); setCreateError(null); setAssignOpen(true); }}
          size="small"
          disabled={saving}
          data-tour="add-member"
        >
          Add Member
        </Button>

        <AddMemberDialog
          open={assignOpen}
          onClose={closeAddDialog}
          onCreate={handleCreateMember}
          onReset={resetAddDialog}
          creating={creating}
          result={createdResult}
          error={createError}
          organization={organization}
        />

        <EditMemberDialog
          open={Boolean(editMember)}
          member={editMember}
          onClose={() => setEditMember(null)}
          onSubmit={handleEditRole}
          saving={saving}
          organization={organization}
        />

        <ConfirmDeleteDialog
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          itemName={deleteConfirm ? `${deleteConfirm.email || deleteConfirm.uid}'s role` : ''}
          onConfirm={handleRemoveRole}
        />
      </AccordionDetails>
    </Accordion>
  );
}

// --- Invite User Dialog ---

function InviteUserDialog({ open, onClose, onSubmit, sites, departments, teams, saving }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('respondent');
  const [scopeId, setScopeId] = useState('');
  const [error, setError] = useState('');

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setEmail('');
      setRole('respondent');
      setScopeId('');
      setError('');
    }
  }, [open]);

  const roleDef = ROLE_DEFINITIONS[role];
  // P4-22: an invited Individual Contributor also needs their NWT (unitForRole → 'team').
  const needsScope = Boolean(unitForRole(role));

  const getScopeOptions = () => {
    // Qualify shared names ("Operations" ×10 sites) with their site/department.
    const siteName = (id) => (sites || []).find((s) => s.id === id)?.name || '';
    if (role === 'site_manager') return (sites || []).map((s) => ({ id: s.id, label: s.name }));
    if (role === 'department_lead') {
      return (departments || []).map((d) => ({ id: d.id, label: `${d.name} — ${siteName(d.siteId)}` }));
    }
    if (role === 'team_lead' || role === 'respondent') {
      return (teams || []).map((t) => {
        const dept = (departments || []).find((d) => d.id === t.departmentId);
        return { id: t.id, label: `${t.name} — ${dept?.name || '?'}, ${siteName(dept?.siteId)}` };
      });
    }
    return [];
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setScopeId('');
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = () => {
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (needsScope && !scopeId) {
      setError(`Please select a ${role === 'respondent' ? 'Natural Work Team (NWT)' : (roleDef.scopeLabel || roleDef.scopeType)}`);
      return;
    }
    const unit = unitForRole(role);
    const team = unit === 'team' ? (teams || []).find((t) => t.id === scopeId) : null;
    const deptId = unit === 'department' ? scopeId : team?.departmentId || null;
    const dept = deptId ? (departments || []).find((d) => d.id === deptId) : null;
    const siteId = unit === 'site' ? scopeId : dept?.siteId || null;
    onSubmit({ email: trimmedEmail, role, scopeId: roleDef?.requiresScopeId ? scopeId || null : null, nwtId: unit === 'team' ? scopeId : null, deptId, siteId });
  };

  const handleClose = () => {
    setEmail('');
    setRole('respondent');
    setScopeId('');
    setError('');
    onClose();
  };

  const scopeOptions = getScopeOptions();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Invite User by Email</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          helperText="The user will see this organization on their next login"
        />
        <TextField
          margin="dense"
          label="Role"
          fullWidth
          required
          select
          SelectProps={{ native: true }}
          value={role}
          onChange={(e) => handleRoleChange(e.target.value)}
        >
          {Object.entries(ROLE_DEFINITIONS).map(([key, def]) => (
            <option key={key} value={key}>
              {def.label}
            </option>
          ))}
        </TextField>
        {needsScope && (
          <TextField
            margin="dense"
            label={`Select ${role === 'respondent' ? 'Natural Work Team (NWT)' : (roleDef.scopeLabel || roleDef.scopeType)}`}
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
          >
            <option value="">Select {role === 'respondent' ? 'Natural Work Team (NWT)' : (roleDef.scopeLabel || roleDef.scopeType)}...</option>
            {scopeOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </TextField>
        )}
        {roleDef && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {roleDef.description}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !email.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}
        >
          Send Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Pending Invites Section ---

function PendingInvitesSection({ orgId, organization, members }) {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  React.useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const unsubscribe = subscribeToOrganizationInvites(orgId, (inv) => {
      setInvites(inv);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [orgId]);

  const handleInviteUser = async ({ email, role, scopeId, nwtId = null, deptId = null, siteId = null }) => {
    setSaving(true);
    setActionError(null);
    try {
      // Validation: check if already a member
      if (isAlreadyMember(email, orgId, members)) {
        setActionError(`${email} is already a member of this organization.`);
        setSaving(false);
        return;
      }

      // Validation: check if already invited
      const existingInvite = await findExistingInvite(email, orgId);
      if (existingInvite) {
        setActionError(`A pending invite already exists for ${email}.`);
        setSaving(false);
        return;
      }

      const roleDef = ROLE_DEFINITIONS[role];
      await createInvite({
        email,
        organizationId: orgId,
        organizationName: organization.name,
        role,
        scopeId,
        scopeType: roleDef?.scopeType || 'organization',
        nwtId,
        deptId,
        siteId,
        invitedBy: user.uid,
      });

      setInviteOpen(false);
      setSuccessMsg(`Invite sent to ${email}`);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!revokeConfirm) return;
    setSaving(true);
    try {
      await revokeInvite(revokeConfirm.id);
      setRevokeConfirm(null);
      setSuccessMsg('Invite revoked');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Invite Users</Typography>
          {invites.length > 0 && (
            <Chip label={`${invites.length} pending`} size="small" color="warning" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Invite users by email to join this organization. They will be assigned the selected role
          and scope when they next log in.
        </Typography>

        <Button
          startIcon={<PersonAddIcon />}
          variant="outlined"
          onClick={() => setInviteOpen(true)}
          disabled={saving}
          sx={{ mb: 2 }}
        >
          Invite User by Email
        </Button>

        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : invites.length > 0 ? (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Pending Invitations
            </Typography>
            <List dense>
              {invites.map((invite) => {
                const roleDef = ROLE_DEFINITIONS[invite.role];
                const expMs = invite.expiresAt ? invite.expiresAt.seconds * 1000 : null;
                const expired = expMs != null && expMs < Date.now();
                const invitedStr = invite.invitedAt
                  ? `Invited ${new Date(invite.invitedAt.seconds * 1000).toLocaleDateString()}`
                  : 'Pending';
                const expStr = expMs != null
                  ? (expired ? ' · Expired' : ` · Expires ${new Date(expMs).toLocaleDateString()}`)
                  : '';
                return (
                  <ListItem key={invite.id} sx={{ opacity: expired ? 0.6 : 1 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <HourglassEmptyIcon fontSize="small" color={expired ? 'disabled' : 'warning'} />
                          <Typography variant="body2">{invite.email}</Typography>
                          <Chip
                            label={roleDef?.label || invite.role}
                            size="small"
                            sx={{
                              bgcolor: `${ROLE_COLORS[invite.role] || '#757575'}15`,
                              color: ROLE_COLORS[invite.role] || '#757575',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                            }}
                          />
                          {expired && <Chip label="Expired" size="small" color="error" variant="outlined" />}
                        </Stack>
                      }
                      secondary={`${invitedStr}${expStr}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => setRevokeConfirm(invite)}
                        disabled={saving}
                        title="Revoke invite"
                      >
                        <CancelIcon fontSize="small" color="error" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No pending invitations.
          </Typography>
        )}

        <InviteUserDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSubmit={handleInviteUser}
          sites={organization.sites}
          departments={organization.departments}
          teams={organization.teams}
          saving={saving}
        />

        <ConfirmDeleteDialog
          open={!!revokeConfirm}
          onClose={() => setRevokeConfirm(null)}
          itemName={revokeConfirm ? `invite for ${revokeConfirm.email}` : ''}
          onConfirm={handleRevokeInvite}
        />

        <Snackbar
          open={!!successMsg}
          autoHideDuration={4000}
          onClose={() => setSuccessMsg('')}
          message={successMsg}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </AccordionDetails>
    </Accordion>
  );
}

function DocumentLibrarySection({ orgId }) {
  const { user, isSuperAdmin } = useAuth();
  const { getUserRole } = useUser();

  // Only visible to org_admin and superadmin
  const userRole = getUserRole(orgId);
  const isOrgAdmin = userRole?.role === 'org_admin';
  const canSeeDocuments = isSuperAdmin() || isOrgAdmin;

  if (!canSeeDocuments) return null;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6">Document Library</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <DocumentUpload
          orgId={orgId}
          userId={user?.uid}
        />
        <Box sx={{ mt: 3 }}>
          <DocumentList orgId={orgId} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * `section` (P4-01): 'organization' = structure, members, invites, library (universal settings);
 * 'drew' = the DREW tab (OE warning thresholds; P2-37 routing, P2-06 site codes, P2-33 defaults
 * join it later); undefined = everything (legacy single page).
 */
function OrganizationDetail({ orgId, section }) {
  const [extendImportOpen, setExtendImportOpen] = useState(false); // P4-14: import more structure into an existing org
  const [rosterOpen, setRosterOpen] = useState(false); // P4-16: Phase 2 roster import
  const showOrg = !section || section === 'organization';
  const showDrew = !section || section === 'drew';
  const { organization, loading } = useOrganization(orgId);
  const actions = useOrganizationActions();

  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [addDeptOpen, setAddDeptOpen] = useState(false);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [editingDept, setEditingDept] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingOrg, setEditingOrg] = useState(false);
  const [editName, setEditName] = useState('');
  const [editIndustry, setEditIndustry] = useState('');
  const [orgMembers, setOrgMembers] = useState([]);

  // Subscribe to members for invite validation
  React.useEffect(() => {
    if (!orgId) return;
    const unsubscribe = subscribeToOrganizationMembers(orgId, (m) => {
      setOrgMembers(m);
    });
    return () => unsubscribe();
  }, [orgId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return <Alert severity="error">Organization not found</Alert>;
  }

  const org = organization;

  const handleEditOrg = () => {
    setEditName(org.name);
    setEditIndustry(org.industry || '');
    setEditingOrg(true);
  };

  const handleSaveOrg = async () => {
    await actions.updateOrganization(orgId, {
      name: editName.trim(),
      industry: editIndustry.trim(),
    });
    setEditingOrg(false);
  };

  const getSiteName = (siteId) => {
    const site = (org.sites || []).find((s) => s.id === siteId);
    return site ? site.name : 'Unknown';
  };

  const getDeptName = (deptId) => {
    const dept = (org.departments || []).find((d) => d.id === deptId);
    return dept ? dept.name : 'Unknown';
  };

  return (
    <Box>
      {actions.actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={actions.clearError}>
          {actions.actionError}
        </Alert>
      )}

      {showOrg && (
        <>
      {/* Organization Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <BusinessIcon color="primary" />
            {editingOrg ? (
              <Box sx={{ flex: 1 }}>
                <TextField
                  size="small"
                  label="Organization Name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <TextField
                  size="small"
                  label="Industry"
                  value={editIndustry}
                  onChange={(e) => setEditIndustry(e.target.value)}
                  fullWidth
                />
                <Stack direction="row" spacing={1} mt={1}>
                  <Button size="small" variant="contained" onClick={handleSaveOrg} disabled={actions.saving}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingOrg(false)}>
                    Cancel
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">{org.name}</Typography>
                {org.industry && (
                  <Typography variant="body2" color="text.secondary">
                    Industry: {org.industry}
                  </Typography>
                )}
              </Box>
            )}
            {!editingOrg && (
              <IconButton onClick={handleEditOrg} size="small">
                <EditIcon />
              </IconButton>
            )}
          </Stack>
          <Stack direction="row" spacing={2} mt={1}>
            <Chip icon={<LocationOnIcon />} label={`${(org.sites || []).length} Sites`} size="small" />
            <Chip icon={<GroupWorkIcon />} label={`${(org.departments || []).length} Departments`} size="small" />
            <Chip icon={<PeopleIcon />} label={`${(org.teams || []).length} NWTs`} size="small" />
          </Stack>
          {section !== 'drew' && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={(e) => { e.currentTarget.blur(); setExtendImportOpen(true); }}>
                Import structure (add sites, departments, NWTs)
              </Button>
              <Button size="small" variant="outlined" startIcon={<PeopleIcon />} onClick={(e) => { e.currentTarget.blur(); setRosterOpen(true); }} data-tour="import-members">
                Import members (people list)
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
      <OrgImportDialog open={extendImportOpen} onClose={() => setExtendImportOpen(false)} existingOrganization={org} onCreated={() => setExtendImportOpen(false)} />
      <RosterImportDialog open={rosterOpen} onClose={() => setRosterOpen(false)} organization={org} />

      {/* Sites */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocationOnIcon color="primary" />
            <Typography variant="h6">Sites</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {(org.sites || []).map((site) => (
              <ListItem key={site.id}>
                <ListItemText
                  primary={site.name}
                  secondary={`${site.location || 'No location'} · code ${isValidSiteCode(site.code) ? site.code : deriveSiteCode(site.name)}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => setEditingSite(site)}
                    sx={{ mr: 0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setDeleteConfirm({ type: 'site', id: site.id, name: site.name })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {(org.sites || []).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No sites added yet.
              </Typography>
            )}
          </List>
          <Button startIcon={<AddIcon />} onClick={() => setAddSiteOpen(true)} size="small">
            Add Site
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Departments (grouped by site) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <GroupWorkIcon color="primary" />
            <Typography variant="h6">Departments</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {(org.sites || []).length > 0 ? (
            <>
              {(org.sites || []).map((site) => {
                const siteDepts = (org.departments || []).filter((d) => d.siteId === site.id);
                return (
                  <Box key={site.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon fontSize="small" sx={{ fontSize: 16 }} />
                      {site.name}
                    </Typography>
                    {siteDepts.length > 0 ? (
                      <List dense sx={{ pl: 2 }}>
                        {siteDepts.map((dept) => (
                          <ListItem key={dept.id}>
                            <ListItemText primary={dept.name} />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                onClick={() => setEditingDept(dept)}
                                sx={{ mr: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setDeleteConfirm({ type: 'department', id: dept.id, name: dept.name })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 0.5, fontStyle: 'italic' }}>
                        No departments in this site
                      </Typography>
                    )}
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                );
              })}
              {/* Show any departments with unassigned/orphaned siteIds */}
              {(() => {
                const siteIds = (org.sites || []).map((s) => s.id);
                const orphanDepts = (org.departments || []).filter((d) => !siteIds.includes(d.siteId));
                if (orphanDepts.length === 0) return null;
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Unassigned
                    </Typography>
                    <List dense sx={{ pl: 2 }}>
                      {orphanDepts.map((dept) => (
                        <ListItem key={dept.id}>
                          <ListItemText primary={dept.name} secondary="Site not found" />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => setEditingDept(dept)}
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setDeleteConfirm({ type: 'department', id: dept.id, name: dept.name })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })()}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No departments added yet.
            </Typography>
          )}
          {(org.departments || []).length === 0 && (org.sites || []).length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No departments added yet.
            </Typography>
          )}
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddDeptOpen(true)}
            size="small"
            disabled={(org.sites || []).length === 0}
          >
            Add Department
          </Button>
          {(org.sites || []).length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Add a site first)
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Teams (grouped by department) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">Natural Work Teams (NWTs)</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {(org.departments || []).length > 0 ? (
            <>
              {(org.departments || []).map((dept) => {
                const deptTeams = (org.teams || []).filter((t) => t.departmentId === dept.id);
                return (
                  <Box key={dept.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GroupWorkIcon fontSize="small" sx={{ fontSize: 16 }} />
                      {dept.name}
                    </Typography>
                    {deptTeams.length > 0 ? (
                      <List dense sx={{ pl: 2 }}>
                        {deptTeams.map((team) => (
                          <ListItem key={team.id}>
                            <ListItemText primary={team.name} />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                onClick={() => setEditingTeam(team)}
                                sx={{ mr: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setDeleteConfirm({ type: 'team', id: team.id, name: team.name })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 0.5, fontStyle: 'italic' }}>
                        No teams in this department
                      </Typography>
                    )}
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                );
              })}
              {/* Show any teams with orphaned departmentIds */}
              {(() => {
                const deptIds = (org.departments || []).map((d) => d.id);
                const orphanTeams = (org.teams || []).filter((t) => !deptIds.includes(t.departmentId));
                if (orphanTeams.length === 0) return null;
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Unassigned
                    </Typography>
                    <List dense sx={{ pl: 2 }}>
                      {orphanTeams.map((team) => (
                        <ListItem key={team.id}>
                          <ListItemText primary={team.name} secondary="Department not found" />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => setEditingTeam(team)}
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setDeleteConfirm({ type: 'team', id: team.id, name: team.name })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })()}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No teams added yet.
            </Typography>
          )}
          {(org.teams || []).length === 0 && (org.departments || []).length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No teams added yet.
            </Typography>
          )}
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddTeamOpen(true)}
            size="small"
            disabled={(org.departments || []).length === 0}
          >
            Add NWT
          </Button>
          {(org.departments || []).length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Add a department first)
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

        </>
      )}

      {/* Thresholds — DREW's own settings (§1c) */}
      {showDrew && (
        <>
          {section === 'drew' && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">{org.name}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  DREW settings for this organization. Structure, members and roles are under the Organization tab.
                </Typography>
              </CardContent>
            </Card>
          )}
          <ThresholdsEditor
            thresholds={org.thresholds}
            onSave={(thresholds) => actions.updateThresholds(orgId, thresholds)}
            saving={actions.saving}
          />
          <ScoringSettingsEditor key={`scoring-${orgId}`} orgId={orgId} scoring={org.scoring} onSave={(scoring) => actions.updateOrganization(orgId, { scoring })} saving={actions.saving} />
          <SurveyRulesEditor org={org} onSave={(patch) => actions.updateOrganization(orgId, patch)} saving={actions.saving} />
        </>
      )}

      {showOrg && (
        <>
          {/* Members & Roles */}
          <MembersRolesSection orgId={orgId} organization={org} />

          {/* Who reviews whose OE Conditions (P2-37) */}
          <ReviewRoutingSection key={orgId} organization={org} saving={actions.saving} onSave={(reviewRouting) => actions.updateOrganization(orgId, { reviewRouting })} />

          {/* View As read-only toggle per level (P4-06) */}
          <ViewAsAccessSection key={`viewas-${orgId}`} organization={org} saving={actions.saving} onSave={(viewAs) => actions.updateOrganization(orgId, { viewAs })} />

          {/* Invite Users */}
          <PendingInvitesSection orgId={orgId} organization={org} members={orgMembers} />

          {/* Learning Library preference (Aug 25): include or exclude the industry library */}
          <LearningLibrarySection key={`library-${orgId}`} organization={org} saving={actions.saving} onSave={(library) => actions.updateLibraryPrefs(orgId, library)} />

          {/* Document Library - org_admin and superadmin only */}
          <DocumentLibrarySection orgId={orgId} />

          {/* API keys + webhooks for this organization (public API; docs at /api-docs) */}
          <ApiAccessSection key={`api-${orgId}`} organizationId={orgId} />
        </>
      )}

      {/* Dialogs */}
      <AddSiteDialog
        sites={org.sites || []}
        open={addSiteOpen}
        onClose={() => setAddSiteOpen(false)}
        onSubmit={async (data) => {
          await actions.addSite(orgId, data);
          setAddSiteOpen(false);
        }}
      />
      <AddDepartmentDialog
        open={addDeptOpen}
        onClose={() => setAddDeptOpen(false)}
        onSubmit={async (data) => {
          await actions.addDepartment(orgId, data);
          setAddDeptOpen(false);
        }}
        sites={org.sites}
      />
      <AddTeamDialog
        open={addTeamOpen}
        onClose={() => setAddTeamOpen(false)}
        onSubmit={async (data) => {
          await actions.addTeam(orgId, data);
          setAddTeamOpen(false);
        }}
        departments={org.departments}
      />
      <EditSiteDialog
        sites={org.sites || []}
        open={!!editingSite}
        onClose={() => setEditingSite(null)}
        site={editingSite}
        onSubmit={async (data) => {
          await actions.updateSite(orgId, editingSite.id, data);
          setEditingSite(null);
        }}
      />
      <EditDepartmentDialog
        open={!!editingDept}
        onClose={() => setEditingDept(null)}
        department={editingDept}
        sites={org.sites}
        onSubmit={async (data) => {
          await actions.updateDepartment(orgId, editingDept.id, data);
          setEditingDept(null);
        }}
      />
      <EditTeamDialog
        open={!!editingTeam}
        onClose={() => setEditingTeam(null)}
        team={editingTeam}
        departments={org.departments}
        onSubmit={async (data) => {
          await actions.updateTeam(orgId, editingTeam.id, data);
          setEditingTeam(null);
        }}
      />
      <ConfirmDeleteDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        itemName={deleteConfirm?.name || ''}
        onConfirm={async () => {
          if (!deleteConfirm) return;
          const { type, id } = deleteConfirm;
          if (type === 'site') await actions.deleteSite(orgId, id);
          else if (type === 'department') await actions.deleteDepartment(orgId, id);
          else if (type === 'team') await actions.deleteTeam(orgId, id);
          setDeleteConfirm(null);
        }}
      />
    </Box>
  );
}

// --- Main OrgSettings Page ---

/**
 * Organization settings. Since P4-01 this renders inside the tabbed Settings page:
 *   <OrgSettings section="organization" embedded />   Settings → Organization
 *   <OrgSettings section="drew" embedded />           Settings → DREW
 * Without props it is the legacy full page (kept for safety; no route points at it any more).
 */
function OrgSettings({ section, embedded = false }) {
  const { user } = useAuth();
  const { organizations, loading } = useUserOrganizations(user?.uid);
  const actions = useOrganizationActions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [deleteOrgConfirm, setDeleteOrgConfirm] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Auto-select the first org if none selected
  React.useEffect(() => {
    if (!selectedOrgId && organizations.length > 0) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  const handleCreateOrg = async ({ name, industry }) => {
    const newOrg = await actions.createOrganization({
      name,
      industry,
      createdBy: user.uid,
    });
    setSelectedOrgId(newOrg.id);
    setCreateDialogOpen(false);
    setSuccessMsg(`Organization "${name}" created successfully!`);
  };

  const handleDeleteOrg = async () => {
    if (!deleteOrgConfirm) return;
    await actions.deleteOrganization(deleteOrgConfirm.id);
    if (selectedOrgId === deleteOrgConfirm.id) {
      setSelectedOrgId(null);
    }
    setDeleteOrgConfirm(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} useFlexGap flexWrap="wrap" gap={1}>
        <Box>
          {!embedded && (
            <Typography variant="h4" gutterBottom>
              Organization Settings
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary">
            {section === 'drew'
              ? 'Organizational Entropy (OE) warning thresholds and other DREW settings, per organization.'
              : 'Set up and manage organization structure, users, and Organizational Entropy (OE) thresholds.'}
          </Typography>
        </Box>
        {section !== 'drew' && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            data-tour="new-org"
          >
            New Organization
          </Button>
        </Stack>
        )}
      </Stack>

      {actions.actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={actions.clearError}>
          {actions.actionError}
        </Alert>
      )}

      {organizations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Organizations Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Create your first organization to get started with DREW.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Organization
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Organization List (left column) */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Your Organizations
            </Typography>
            {organizations.map((org) => (
              <Card
                key={org.id}
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  border: selectedOrgId === org.id ? 2 : 1,
                  borderColor: selectedOrgId === org.id ? 'primary.main' : 'divider',
                }}
                onClick={() => setSelectedOrgId(org.id)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {org.name}
                  </Typography>
                  {org.industry && (
                    <Typography variant="caption" color="text.secondary">
                      {org.industry}
                    </Typography>
                  )}
                </CardContent>
                {section !== 'drew' && (
                  <CardActions sx={{ pt: 0 }}>
                    <Button
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteOrgConfirm(org);
                      }}
                    >
                      Delete
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Grid>

          {/* Organization Detail (right column) */}
          <Grid item xs={12} md={9}>
            {selectedOrgId ? (
              <OrganizationDetail orgId={selectedOrgId} section={section} />
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    Select an organization to manage
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      <CreateOrgDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrg}
        onImport={() => { setCreateDialogOpen(false); setImportDialogOpen(true); }}
      />

      <OrgImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onCreated={(org) => {
          setSelectedOrgId(org.id);
          setSuccessMsg(`Organization "${org.name}" imported successfully!`);
        }}
      />

      <ConfirmDeleteDialog
        open={!!deleteOrgConfirm}
        onClose={() => setDeleteOrgConfirm(null)}
        itemName={deleteOrgConfirm?.name || ''}
        onConfirm={handleDeleteOrg}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default OrgSettings;
